import React, { useMemo, useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatToLocalDateTime } from '../../services/dateTimeService';

export type StageState = "completed" | "in-progress" | "not-started";

export interface StageReviewer {
  reviewerName?: string | null;
  reviewStatus?: string | null;
  reviewState?: string | null;
  reviewStatusCode?: string | null;
  reviewSequence?: number | null;
  reviewerId?: string | null;
  [key: string]: unknown;
}

export interface ReviewActivityLog {
  activityLogId?: number | null;
  reviewId?: number | null;
  disputeId?: number | null;
  statusId?: number | null;
  mpiId?: number | null;
  activityLogDescription?: string | null;
  createdBy?: string | null;
  createDate?: string | null;
  updatedBy?: string | null;
  updateDate?: string | null;
}

export interface ReviewerPopupField {
  label: string;
  value: string | null | undefined;
  fallback?: string;
}

interface ReviewStageTrackerComponentProps {
  /**
   * Reviewer records. Each record drives a node in the tracker.
   * Stage label, code, state, and order are all derived from these objects.
   */
  reviewers: StageReviewer[];
  /** Title shown above the tracker. */
  title?: string;
  /** Subtitle/help text shown above the tracker. */
  subtitle?: string;
  /**
   * Optional override that maps a `reviewState` string to a visual state.
   * Defaults to: COMPLETED → completed, INPROGRESS → in-progress, anything else → not-started.
   */
  resolveStageState?: (reviewState: string | null | undefined) => StageState;
  /** Builds the popup body for a reviewer. Defaults to Name/Status/State fields. */
  buildReviewerFields?: (reviewer: StageReviewer) => ReviewerPopupField[];
  /** Message shown when there are no reviewers at all. */
  emptyTrackerMessage?: string;
  /** Default fallback for missing field values. */
  defaultFallback?: string;
  /** Callback to fetch activity logs when a stage node is clicked. */
  fetchActivityLogs?: (reviewer: StageReviewer) => Promise<ReviewActivityLog[]>;
  activityLogClassName?: string;
}

const defaultResolveStageState = (
  reviewState: string | null | undefined
): StageState => {
  const s = reviewState?.toLowerCase();
  if (s === "completed") return "completed";
  if (s === "inprogress" || s === "in-progress" || s === "in_progress")
    return "in-progress";
  return "not-started";
};

const ReviewStageTrackerComponent: React.FC<ReviewStageTrackerComponentProps> = ({
  reviewers,
  title,
  subtitle,
  resolveStageState = defaultResolveStageState,
  emptyTrackerMessage,
  defaultFallback = "-",
  fetchActivityLogs,
  activityLogClassName,
}) => {
  const [selectedReviewer, setSelectedReviewer] = useState<StageReviewer | null>(
    null
  );
  const [activityLogs, setActivityLogs] = useState<ReviewActivityLog[]>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogsError, setActivityLogsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setActivityLogs([]);
    setActivityLogsError(null);
    setActivityLogsLoading(false);
    if (selectedReviewer && fetchActivityLogs) {
      setActivityLogsLoading(true);
      fetchActivityLogs(selectedReviewer)
        .then((logs) => {
          if (!cancelled) setActivityLogs(Array.isArray(logs) ? logs : []);
        })
        .catch(() => {
          if (!cancelled) setActivityLogsError("Unable to load activity logs. Please try again.");
        })
        .finally(() => {
          if (!cancelled) setActivityLogsLoading(false);
        });
    }
    return () => { cancelled = true; };
  }, [selectedReviewer, fetchActivityLogs]);

  // Merge reviewers sharing the same reviewStatusCode (concatenating reviewer names),
  // then order by reviewSequence (fallback to original order for items without a sequence).
  const orderedReviewers = useMemo(() => {
    const list = (reviewers || []).filter(Boolean);

    const statusCodeMap = new Map<string, StageReviewer>();
    const merged: StageReviewer[] = [];

    list.forEach((reviewer) => {
      const code = reviewer.reviewStatusCode;
      if (code && statusCodeMap.has(code)) {
        const existing = statusCodeMap.get(code)!;
        const existingNames = (existing.reviewerName ?? "")
          .split(",")
          .map((n) => n.trim())
          .filter(Boolean);
        const namesSet = new Set(existingNames);
        const incomingName = reviewer.reviewerName?.trim();
        if (incomingName && !namesSet.has(incomingName)) {
          existing.reviewerName = existingNames.length
            ? `${existing.reviewerName}, ${incomingName}`
            : incomingName;
        }
      } else {
        const copy = { ...reviewer };
        if (code) {
          statusCodeMap.set(code, copy);
        }
        merged.push(copy);
      }
    });

    // Replace entries in `merged` with the latest merged version from the map
    // so concatenated names are reflected.
    const deduped = merged.map((r) => {
      const code = r.reviewStatusCode;
      return code && statusCodeMap.has(code) ? statusCodeMap.get(code)! : r;
    });

    return deduped.sort((a, b) => {
      const aSeq =
        typeof a.reviewSequence === "number"
          ? a.reviewSequence
          : Number.MAX_SAFE_INTEGER;
      const bSeq =
        typeof b.reviewSequence === "number"
          ? b.reviewSequence
          : Number.MAX_SAFE_INTEGER;
      return aSeq - bSeq;
    });
  }, [reviewers]);

  return (
    <div className="review-stage-right">
      {(title || subtitle) && (
        <div className="review-stage-tracker-header">
          {title && <div className="review-stage-tracker-title">{title}</div>}
          {subtitle && (
            <div className="review-stage-tracker-subtitle">{subtitle}</div>
          )}
        </div>
      )}

      {orderedReviewers.length === 0 ? (
        emptyTrackerMessage ? (
          <div className="review-stage-tracker-empty">{emptyTrackerMessage}</div>
        ) : null
      ) : (
        <div className="review-stage-tracker-nodes">
          {orderedReviewers.map((reviewer, idx) => {
            const state = resolveStageState(reviewer.reviewState);
            const label = reviewer.reviewStatus ?? "";
            const key =
              reviewer.reviewStatusCode ??
              `${reviewer.reviewerId ?? reviewer.reviewerName ?? "reviewer"}-${idx}`;
            const isSelected = selectedReviewer === reviewer;

            return (
              <React.Fragment key={String(key)}>
                <button
                  type="button"
                  className={`tracker-node tracker-node-${state}${
                    isSelected ? " tracker-node-selected" : ""
                  }`}
                  onClick={() => setSelectedReviewer(reviewer)}
                  aria-label={`${label} - ${state}`}
                  aria-haspopup="dialog"
                  aria-expanded={isSelected}
                >
                  <span className="tracker-node-circle">
                    {state === "completed" && <i className="pi pi-check" />}
                    {state === "in-progress" && (
                      <span className="tracker-node-dot" />
                    )}
                  </span>
                  <span className="tracker-node-label">{label}</span>
                </button>
                {idx < orderedReviewers.length - 1 && (
                  <span
                    className={`tracker-connector ${
                      state === "completed" ? "tracker-connector-done" : ""
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      <Dialog
        header={`${selectedReviewer?.reviewStatus ?? ""} – User Activity Log`}
        visible={!!selectedReviewer}
        onHide={() => setSelectedReviewer(null)}
        style={{ width: "640px", maxWidth: "calc(100vw - 2rem)" }}
        modal
        closable
        closeOnEscape
        draggable={false}
        resizable={false}
        dismissableMask={false}
        className={["activity-log-dialog", activityLogClassName].filter(Boolean).join(" ")}
      >
        {selectedReviewer && (
          <div className="activity-log-table-wrapper">
            <DataTable
              value={activityLogs}
              loading={activityLogsLoading}
              sortField="createDate"
              sortOrder={-1}
              emptyMessage={activityLogsError ? <span role="alert">{activityLogsError}</span> : "No activity logs found."}
              size="small"
              scrollable
              scrollHeight="300px"
            >
              <Column
                field="createDate"
                header="DATE/TIME (LOCAL)"
                sortable
                body={(rowData: ReviewActivityLog) => {
                  if (!rowData.createDate) return defaultFallback;
                  return formatToLocalDateTime(rowData.createDate) || defaultFallback;
                }}
              />
              <Column
                field="createdBy"
                header="USER"
                sortable
                filter
                filterMatchMode="contains"
                filterPlaceholder="Search user"
                body={(rowData: ReviewActivityLog) =>
                  rowData.createdBy?.trim() || defaultFallback
                }
              />
              <Column
                field="activityLogDescription"
                header="ACTION"
                body={(rowData: ReviewActivityLog) =>
                  rowData.activityLogDescription ?? defaultFallback
                }
              />
            </DataTable>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default ReviewStageTrackerComponent;
